import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, Swords } from 'lucide-react';
import { useTournamentStore } from '../../store/tournament-store';
import { useArenaStore } from '../../store/arena-store';
import {
  garaDaAnnunciare,
  laMiaArenaEAttiva,
  garaTieneAccesoLoSchermo,
  laPaginaEDellaGara,
  type StatoGara,
  type PaginaCorrente,
} from '../../utils/gara-in-corso';
import { useSchermoSveglio } from '../../hooks/useSchermoSveglio';
import { useAltezzaBandaGara } from '../../hooks/useAltezzaBandaGara';
import './BandaGaraInCorso.css';

/**
 * «Sei in una gara in corso» — il promemoria che si legge da ogni schermata.
 *
 * Una gara sopravvive alla pagina da cui ci si e' iscritti, ed e' il punto: si
 * aspetta il turno leggendo le regole, risolvendo un problema o guardando la
 * classifica di qualcun altro. Il rovescio e' che il server accoppia chi e'
 * CONNESSO, non chi sta guardando la pagina del torneo — App.tsx lo dice a
 * parole sue nell'effetto di instradamento: «chi aspettava il turno sulla Home,
 * sui Puzzle, sulle Regole o perfino sull'ELENCO dei tornei veniva accoppiato e
 * non lo scopriva mai: 25 schermate su 28». Quell'effetto porta alla partita
 * quando arriva; questo banner dice che sta per arrivare.
 *
 * Fratello di `QueuedTableBanner`, da cui prende il vestito: la riga e' un
 * bottone che riporta alla gara, i selettori sono campi scalari perche' e'
 * montato su una quarantina di rami di render, e l'unico cancello e' il ritorno
 * anticipato.
 *
 * Due cose del fratello NON si ricalcano:
 *
 * - **la ✕**. Da un torneo a turni avviato non si esce: il server rifiuta
 *   `swiss:leave` con `ROUNDS_STARTED`. Offrire un gesto che verra' rifiutato e'
 *   peggio che non offrirlo — l'uscita, dove e' possibile, vive nella pagina
 *   della gara, che e' dove la riga porta.
 * - **la guardia di visibilita' che cancella l'iscrizione** dopo cinque secondi
 *   in secondo piano. Quella e' della coda: da un torneo non si esce per un
 *   alt-tab.
 *
 * E tiene acceso lo schermo mentre si aspetta (`useSchermoSveglio`): e' la
 * superficie dove lo spegnimento automatico costa di piu', perche' qui non si
 * tocca niente per minuti e la partita arriva da sola.
 */
interface Props {
  /** Porta alla scheda del torneo. L'id lo decide chi chiama: qui si sa solo che ce n'e' uno. */
  onApriTorneo?: (tournamentId: string) => void;
  /** Porta alla scheda dell'arena. */
  onApriArena?: (arenaId: string) => void;
  /**
   * La schermata aperta adesso, per non annunciare una gara a chi la sta gia'
   * guardando. Facoltativa: senza, il banner si comporta come prima — e i test
   * che lo montano da soli non devono inventarsi una posizione.
   */
  pagina?: PaginaCorrente | null;
}

export default function BandaGaraInCorso({ onApriTorneo, onApriArena, pagina }: Props) {
  const { t } = useTranslation();

  /*
   * Campi scalari, mai oggetti ne' liste.
   *
   * Questo banner e' montato su OGNI schermata, accanto alle sovrimpressioni
   * delle sfide. Abbonarsi a `currentTournament` o a `standings` vorrebbe dire
   * risvegliarlo a ogni aggiornamento della classifica — che durante un turno
   * arrivano da soli — per poi ridisegnare le stesse due righe. E' la stessa
   * ragione, scritta per esteso, del banner del tavolo in coda.
   *
   * L'unica eccezione e' `laMiaArenaEAttiva`, e non la contraddice: quel
   * selettore LEGGE `arenas` ma restituisce un BOOLEANO, quindi si risveglia
   * come gli altri e ridisegna solo quando la risposta cambia. Chi lo
   * «correggesse» in un selettore di oggetti rimetterebbe in piedi proprio il
   * difetto che questa nota descrive.
   */
  const torneoIscritto = useTournamentStore((s) => s.isJoined);
  const torneoStato = useTournamentStore((s) => s.status);
  const torneoIscrittoId = useTournamentStore((s) => s.torneoIscrittoId);
  const torneoInPaginaId = useTournamentStore((s) => s.currentTournament?.id ?? null);
  const torneoInPaginaNome = useTournamentStore((s) => s.currentTournament?.name ?? null);
  const turno = useTournamentStore((s) => s.currentRound);
  const turniTotali = useTournamentStore((s) => s.totalRounds);

  const arenaIscrittaId = useArenaStore((s) => s.arenaIscrittaId);
  const arenaStato = useArenaStore((s) => s.status);
  const arenaInPausa = useArenaStore((s) => s.myParticipant?.status === 'paused');
  const arenaIscrittaAttiva = useArenaStore(laMiaArenaEAttiva);
  const arenaInPaginaId = useArenaStore((s) => s.currentArena?.id ?? null);
  const arenaInPaginaNome = useArenaStore((s) => s.currentArena?.name ?? null);

  const stato: StatoGara = { torneoIscritto, torneoStato, arenaIscrittaId, arenaStato, arenaInPausa, arenaIscrittaAttiva };
  const quale = garaDaAnnunciare(stato);

  /* Prima del ritorno anticipato: gli hook non si saltano. */
  useSchermoSveglio(garaTieneAccesoLoSchermo(stato));
  /*
   * E la propria altezza alle pagine, che ci si riservano lo spazio in fondo:
   * il banner e' `fixed` e non occupa flusso, quindi senza questa misura
   * l'ultima riga di una pagina gli finisce sotto. A banner spento l'elemento
   * e' `null`, la variabile sparisce e la riserva si sgonfia da sola.
   */
  const [riga, setRiga] = useState<HTMLElement | null>(null);
  useAltezzaBandaGara(riga);

  if (quale === null) return null;

  /*
   * Il secondo cancello: sulla pagina della propria gara il banner tace.
   *
   * Sta QUI sotto, e non piu' in alto, perche' `useSchermoSveglio` non si
   * salta: su quelle pagine il banner non si disegna ma lo schermo deve
   * restare acceso lo stesso — la partita arriva da sola anche mentre si
   * guarda la classifica. Un cancello sopra l'hook sarebbe un hook in meno nel
   * ramo sbagliato, ed e' esattamente cio' che `useSchermoSveglio.cablaggio`
   * misura.
   */
  if (laPaginaEDellaGara(quale, { torneoIscrittoId, arenaIscrittaId }, pagina ?? null)) return null;

  if (quale === 'torneo') {
    /*
     * Il nome solo se e' DAVVERO il suo.
     *
     * `currentTournament` e' il torneo che si sta GUARDANDO, `torneoIscrittoId`
     * quello a cui si e' iscritti, e «puo' NON essere quello in pagina» — lo
     * dice lo store. Aprire dall'elenco il torneo di qualcun altro mentre si
     * aspetta il proprio turno e' permesso, e in quel momento il nome in pagina
     * sarebbe quello sbagliato: meglio nessun nome che il nome di un'altra
     * gara.
     */
    const id = torneoIscrittoId ?? torneoInPaginaId;
    const nome = torneoIscrittoId !== null && torneoIscrittoId === torneoInPaginaId ? torneoInPaginaNome : null;
    /* `totalRounds` e' 0 finche' il server non ha fissato i turni: in quel caso
       non si annuncia un «turno 1 di 0». */
    const dettaglio = turniTotali > 0
      ? t('tournament.swiss.roundOf', { current: turno, total: turniTotali })
      : null;

    return (
      <div className="banda-gara" role="status" ref={setRiga}>
        <button
          type="button"
          className="banda-gara-riga"
          aria-label={t('gara.torneoInCorso')}
          disabled={!id || !onApriTorneo}
          onClick={() => { if (id) onApriTorneo?.(id); }}
        >
          <span className="banda-gara-ico" aria-hidden="true"><Trophy size={18} /></span>
          <span className="banda-gara-testo">
            {t('gara.torneoInCorso')}
            {(nome || dettaglio) && (
              <span className="banda-gara-dettaglio">{[nome, dettaglio].filter(Boolean).join(' · ')}</span>
            )}
          </span>
        </button>
      </div>
    );
  }

  const nomeArena = arenaIscrittaId === arenaInPaginaId ? arenaInPaginaNome : null;
  /* In pausa si e' dentro ma fuori dal giro: dirlo, o il banner prometterebbe
     un avversario che non puo' arrivare. */
  const dettaglioArena = arenaInPausa ? null : t('arena.band.searchingTitle');

  return (
    <div className="banda-gara" role="status" ref={setRiga}>
      <button
        type="button"
        className="banda-gara-riga"
        aria-label={t('gara.arenaInCorso')}
        disabled={!arenaIscrittaId || !onApriArena}
        onClick={() => { if (arenaIscrittaId) onApriArena?.(arenaIscrittaId); }}
      >
        <span className="banda-gara-ico" aria-hidden="true"><Swords size={18} /></span>
        <span className="banda-gara-testo">
          {t('gara.arenaInCorso')}
          {(nomeArena || dettaglioArena) && (
            <span className="banda-gara-dettaglio">{[nomeArena, dettaglioArena].filter(Boolean).join(' · ')}</span>
          )}
        </span>
      </button>
    </div>
  );
}
